import type { FormRule } from "antd";
import { type ReactNode, useMemo } from "react";
import { useIntl } from "react-intl";
import { noLeadingOrTrailingSpacesRule } from "~utils/noLeadingOrTrailingSpacesRule";

export type UseFormItemRulesParams = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  noLeadingOrTrailingSpaces?: boolean;
  fieldLabel?: ReactNode;
};

export const useFormItemRules = ({
  required,
  minLength,
  maxLength,
  noLeadingOrTrailingSpaces,
  fieldLabel,
}: UseFormItemRulesParams): FormRule[] => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const rules: FormRule[] = [];

    if (required) {
      rules.push({
        required: true,
        message: formatMessage({ id: "form.help.required.field" }, { field: fieldLabel as string }),
      });
    }

    if (minLength) {
      rules.push({
        min: minLength,
        message: formatMessage({ id: "form.validation.min.field.length" }, { fieldName: fieldLabel as string, length: minLength }),
      });
    }

    if (maxLength) {
      rules.push({
        max: maxLength,
        message: formatMessage({ id: "form.validation.max.field.length" }, { fieldName: fieldLabel as string, length: maxLength }),
      });
    }

    if (noLeadingOrTrailingSpaces) {
      rules.push(noLeadingOrTrailingSpacesRule(formatMessage));
    }

    return rules;
  }, [fieldLabel, formatMessage, maxLength, minLength, noLeadingOrTrailingSpaces, required]);
};
